import { Intent, NonIdealState, Spinner, SpinnerSize } from '../../components/ui';
import React, { useEffect, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import VisibilitySensor from 'react-visibility-sensor';
import BriefInProgress from '../../components/brief-in-progress/BriefInProgress';
import BriefNavBar from '../../components/brief-nav-bar/briefNavBar';
import BriefStoryCard from '../../components/brief-story-card/BriefStoryCard';
import DeleteBriefDialog from '../../components/delete-brief-dialog/DeleteBriefDialog';
import EditBriefPanel from '../../components/edit-brief-panel/EditBriefPanel';
import InactiveSubscription from '../../components/inactive-subscription/inactiveSubscription';
import LoadingSpinner from '../../components/loading-spinner/LoadingSpinner';
import { ReaderPanel } from '../../components/reader-panel/readerPanel';
import { StoryCardSkeleton } from '../../components/uikit/story-card-skeleton/storyCardSkeleton';
import { useAccount } from '../../hooks/useAccount';
import { useBriefsOverview, useDestroyBrief } from '../../hooks/useBriefs';
import { StoryClickContext, useBriefStories } from '../../hooks/useBriefStories';
import DashboardPageLayout from '../../layouts/dashboard/dashboardPageLayout';
import { locationToSidebarTool, SidebarTool } from '../../utils/SidebarTool';
import { Brief } from '../../services/models/brief';
import { BriefStatus } from '../../services/models/brief-status';
import { Story } from '../../services/models/story';

type PageParams = {
  briefId: string;
};

const BriefPage: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  let { briefId } = useParams<PageParams>();
  const briefIdInt = Number(briefId);

  const { account } = useAccount();
  const subscription = account?.subscription;
  const { briefs: allBriefs, loading: isFetchingBriefs } = useBriefsOverview();
  const briefs: Brief[] = (allBriefs || []).filter(brief => brief.status !== BriefStatus.Draft);

  const brief = briefs.find(x => x.id === briefIdInt);
  const briefFound = !!brief;
  const briefStatus = brief ? brief.status : undefined;

  const briefDetailsHash = JSON.stringify([brief?.keywords, brief?.language]);

  const canAddMoreBriefs = briefs.length < (account?.product?.features.briefs || 0);

  const isFetching = allBriefs === undefined || isFetchingBriefs;

  const {
    stories,
    hasMoreStories,
    lastLoadedStoryCursor,
    isFetchingStories,
    loadStoriesPage,
    loadStoryTweets,
    patchStory,
  } = useBriefStories();

  const { destroyBrief, isDestroying } = useDestroyBrief();

  const [tool, setTool] = useState(SidebarTool.NONE);
  const [openEditPanel, setOpenEditPanel] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showDestroyPrompt, setShowDestroyPrompt] = useState(false);
  const [readerAppConf, setReaderAppConf] = useState<{ storyId: number; storyUri: string }>();

  useEffect(() => {
    setTool(locationToSidebarTool(location.pathname));
  }, [location, setTool]);

  useEffect(() => {
    setOpenEditPanel(tool !== SidebarTool.NONE);
  }, [tool, setOpenEditPanel]);

  useEffect(() => {
    if (
      briefFound &&
      briefStatus &&
      briefStatus !== BriefStatus.InProgress &&
      !!subscription?.active
    ) {
      loadStoriesPage(briefIdInt, 0);
    }
    // briefDetailsHash re-triggers the first page when keywords/language
    // change, matching the legacy effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [briefFound, briefStatus, briefIdInt, loadStoriesPage, briefDetailsHash, subscription]);

  if (isFetching) {
    return <LoadingSpinner />;
  }

  if (!brief) {
    return <Redirect to={`/dashboard`} />;
  }

  if (brief.status === BriefStatus.InProgress) {
    return <BriefInProgress canAddMoreBriefs={canAddMoreBriefs} />;
  }

  const handleEditPanelClose = () => {
    const pathName = location.pathname;

    const newPathName = pathName
      .replace('edit', '')
      .replace('slack', '')
      .replace('schedule', '')
      .replace('recipients', '')
      .replace('integrations', '');

    history.push(newPathName);
  };

  const requestStoryTweetsHandler = (story: Story) => {
    if (!isFetching && !story.isFetchingTweets) {
      loadStoryTweets(brief.id, story.id);
    }
  };

  const getContent = () => {
    if (subscription && !subscription.active) {
      return (
        <InactiveSubscription
          subscription={subscription}
          title="Thank you for trying Newslit!"
          description="You've reached the end of your free trial. To access your News Brief, daily digests and more please select one of our paid plans."
        />
      );
    }

    if (!stories || stories.length === 0) {
      if (!stories || !!isFetchingStories) {
        return (
          <div className="flex flex-col h-full justify-center items-center">
            <Spinner intent={Intent.PRIMARY} size={SpinnerSize.STANDARD} />
            <h2 className="text-xl font-bold pt-3 md:text-2xl">Loading stories</h2>
            <p className="text-base px-6 pt-3 text-center">
              We are looking matching stories for your report.
            </p>
          </div>
        );
      }

      return (
        <div className="flex h-screen items-center justify-center">
          <NonIdealState
            icon="search"
            title="No recent stories"
            description="Sorry, no stories have been found matching your keywords at the moment. You can check again by refreshing at
        a later time."
          />
        </div>
      );
    }

    return (
      <div className="block sm:flex sm:h-full sm:overflow-y-auto">
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {stories.map(story => (
            <li key={story.id} className="col-span-1 bg-white">
              <BriefStoryCard
                key={story.id}
                story={story}
                briefId={brief.id}
                titleClick={(storyId, storyUri) => {
                  setIsDrawerOpen(true);
                  setReaderAppConf({ storyId, storyUri });
                }}
                requestStoryTweets={s => requestStoryTweetsHandler(s)}
              />
            </li>
          ))}

          {/* Infinite Scroll Indicator */}
          {hasMoreStories && (
            <VisibilitySensor
              partialVisibility
              onChange={(visible: boolean) => {
                if (visible) {
                  loadStoriesPage(brief.id, lastLoadedStoryCursor || 0);
                }
              }}
            >
              <>
                <li className="col-span-1 bg-white">
                  <StoryCardSkeleton />
                </li>
                <li className="col-span-1 bg-white">
                  <StoryCardSkeleton />
                </li>
                <li className="col-span-1 bg-white">
                  <StoryCardSkeleton />
                </li>
              </>
            </VisibilitySensor>
          )}
        </ul>
      </div>
    );
  };

  return (
    <StoryClickContext.Provider
      value={{ onStoryViewed: storyId => patchStory(storyId, { isClicked: true }) }}
    >
      <DashboardPageLayout title={brief.name}>
        {/* main area */}
        <main className="flex flex-1 z-0 overflow-y-auto overflow-hidden bg-white focus:outline-hidden">
          {/* Primary section */}
          <section className="flex-1 h-full relative flex flex-col">
            {/* Brief Navbar */}
            <div className="shrink-0 z-20">
              <div className="h-12 sm:hidden"></div>
              <div className="fixed z-10 top-0 right-0 w-full sm:relative sm:top-auto sm:right-auto">
                <BriefNavBar brief={brief} onDeleteBrief={() => setShowDestroyPrompt(true)} />
              </div>
            </div>

            {getContent()}
          </section>
        </main>

        <EditBriefPanel
          account={account!}
          brief={brief}
          tool={tool}
          panelOpen={openEditPanel}
          onClosePanel={handleEditPanelClose}
        />

        <ReaderPanel
          url={readerAppConf?.storyUri ?? null}
          storyId={readerAppConf?.storyId ?? null}
          show={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />

        <DeleteBriefDialog
          brief={brief}
          isDestroying={isDestroying}
          open={showDestroyPrompt}
          onAccept={async () => {
            const destroyed = await destroyBrief(brief.id);
            if (destroyed) {
              setShowDestroyPrompt(false);
            }
          }}
          onCancel={() => setShowDestroyPrompt(false)}
        />
      </DashboardPageLayout>
    </StoryClickContext.Provider>
  );
};

export default BriefPage;
